import CheckIcon from "@mui/icons-material/Check";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import {
  Box,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import { ProgressButton, useToast, DialogRoot } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { AutostoreBin } from "~/features/autostoreBin";

import useWindowDimensions from "~/lib/browserDimensions";
import { ternaryIff } from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";

import { closeBinConfigModal } from "./autostoreBinConfigModal.slice";

type AutostoreBinConfigModalProps = {
  dimensionsCallback?: (dimensions: {
    numOfRows: number;
    numOfCols: number;
  }) => void;
  saveBinConfigurations: (selectedConfig: number[]) => void;
  currentBinConfigurations: number[];
  isOneSelection?: boolean;
};

export function AutostoreBinConfigModal(props: AutostoreBinConfigModalProps) {
  const {
    dimensionsCallback,
    currentBinConfigurations,
    saveBinConfigurations,
    isOneSelection
  } = props;
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(
    (state) => state.autostoreBinConfigModal.isOpen
  );
  const {
    data: availableBinConfigurations,
    error: availableBinConfigurationsError
  } = useGetBinConfigurationsQuery();

  const { width } = useWindowDimensions();
  const browserWidth =
    width < 1280
      ? ternaryIff<"xs" | "sm" | "md">(
          width > 960,
          "md",
          ternaryIff<"xs" | "sm">(width > 600, "sm", "xs")
        )
      : "lg";

  const { t } = useTranslation();
  const { errorToast } = useToast();
  const trackedModalName = useMixpanelModalName("Select Bin Configuration");

  const [selectedConfigurations, setSelectedConfigurations] = useState<
    number[]
  >([]);

  useEffect(() => {
    if (availableBinConfigurationsError) {
      const errorMessage = getMessageFromRtkError(
        availableBinConfigurationsError
      );
      errorToast(
        `Error occurred when getting available bin configurations: ${errorMessage}`,
        {
          trackingLocation: trackedModalName
        }
      );
    }
  }, [availableBinConfigurationsError, errorToast, trackedModalName]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (currentBinConfigurations)
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setSelectedConfigurations(currentBinConfigurations);
  }, [currentBinConfigurations]);

  const selectAllConfigurations = () => {
    if (availableBinConfigurations)
      setSelectedConfigurations(
        availableBinConfigurations.map((b) => b.configurationType)
      );
  };

  return (
    <DialogRoot
      fullWidth
      maxWidth={browserWidth}
      onClose={(_event, reason) => {
        dispatch(closeBinConfigModal());
        mixpanelTrackModalDismiss(trackedModalName, reason);
      }}
      aria-labelledby="simple-dialog-title"
      open={isOpen}
    >
      <DialogTitle id="simple-dialog-title">
        <Typography sx={{ fontSize: "24px" }}>
          {t("select bin configuration")}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          boxSizing: "border-box",
          my: 2,
          display: "grid",
          flexDirection: "row",
          justifyContent: "center",
          gridTemplateColumns: `repeat(${availableBinConfigurations?.length || 1}, minmax(210px, 400px))`,
          gap: {
            xs: 2,
            lg: 3
          },
          width: "100%"
        }}
      >
        {availableBinConfigurations?.map((conf) => (
          <Box
            key={conf.numberOfCompartments}
            role="option"
            aria-label={`binconfiguration-${conf.configurationType}`}
            aria-selected={selectedConfigurations.includes(
              conf.configurationType
            )}
            onClick={() => {
              if (isOneSelection) {
                setSelectedConfigurations([conf.configurationType]);
              } else if (
                selectedConfigurations.includes(conf.configurationType)
              ) {
                setSelectedConfigurations(
                  selectedConfigurations.filter(
                    (selectedConf) => selectedConf !== conf.configurationType
                  )
                );
              } else {
                setSelectedConfigurations([
                  ...selectedConfigurations,
                  conf.configurationType
                ]);
              }
              if (dimensionsCallback) {
                dimensionsCallback({
                  numOfRows: conf.horizontalCompartmentCount,
                  numOfCols: conf.verticalCompartmentCount
                });
              }
              mixpanelTrack({
                type: "Button Click",
                label: "Select Bin Configuration",
                trackedPageName: trackedModalName,
                eventProperties: {
                  "Configuration Type": conf.configurationType,
                  "Number Of Compartments": conf.numberOfCompartments
                }
              });
            }}
            sx={{
              display: "flex",
              width: "100%",
              my: 2
            }}
          >
            <Box
              sx={{
                flex: 1,
                ...(selectedConfigurations.includes(conf.configurationType)
                  ? {
                      padding: 0,
                      borderRadius: "0.5em",
                      outline: `0.625em solid`,
                      outlineColor: "borderPrimary.main",
                      outlineOffset: "2px"
                    }
                  : {})
              }}
            >
              <AutostoreBin
                state={"Port Closed"}
                pickQuantity={0}
                pickCompartment={null}
                numberOfRows={conf.horizontalCompartmentCount}
                numberOfColumns={conf.verticalCompartmentCount}
                hideBinId
              />
            </Box>
          </Box>
        ))}
      </DialogContent>
      <Stack
        flexDirection="row"
        justifyContent={isOneSelection ? "flex-end" : "space-between"}
        px={4}
        py={1}
        gap={1}
      >
        {!isOneSelection && (
          <ProgressButton
            buttonSize="footer"
            emphasis="high"
            responsive
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<SelectAllIcon style={{ fontSize: 22 }} />}
            onClick={() => {
              selectAllConfigurations();
              mixpanelTrack({
                type: "Button Click",
                label: "Select All",
                trackedPageName: trackedModalName
              });
            }}
          >
            {t("select all")}
          </ProgressButton>
        )}

        <ProgressButton
          buttonSize="footer"
          emphasis="high"
          responsive
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<CheckIcon style={{ fontSize: 22 }} />}
          onClick={() => {
            saveBinConfigurations(selectedConfigurations);
            dispatch(closeBinConfigModal());
            mixpanelTrack({
              type: "Button Click",
              label: "Confirm",
              trackedPageName: trackedModalName,
              eventProperties: {
                "Selected Configurations": selectedConfigurations
              }
            });
          }}
          disabled={!selectedConfigurations.length}
        >
          {t("confirm")}
        </ProgressButton>
      </Stack>
    </DialogRoot>
  );
}
